import {
  CardsData,
  ConsentDatum,
  IEDatum,
  IndicationEventDatum,
  CardType,
  FullRecordConfig,
} from "./cards.types";

const addNullFallbackRender = <
  T extends { title: string; dataIndex: string; key: string },
>(
  columns: T[],
) =>
  columns.map((col) => ({
    ...col,
    render: (value: unknown) => value ?? "-",
  }));

const buildFullRecordConfig = <T>(
  rows: T[],
  columns: { title: string; dataIndex: string; key: string }[],
): FullRecordConfig => {
  return {
    columns: addNullFallbackRender(columns) as FullRecordConfig["columns"],
    dataSource: rows as unknown as Record<string, unknown>[],
  };
};

interface GetFullRecordConfigParams {
  cardsData: CardsData;
  ieData: IEDatum[];
  consentData: ConsentDatum[];
  indicationEventData: IndicationEventDatum[];
}

export const getFullRecordConfig = (
  cardKey: CardType,
  {
    cardsData,
    ieData,
    consentData,
    indicationEventData,
  }: GetFullRecordConfigParams,
): FullRecordConfig => {
  switch (cardKey) {
    case "screening":
      return buildFullRecordConfig(ieData, [
        {
          title: "Eligibility Criteria Failed",
          dataIndex: "ie_criteria_not_met",
          key: "ie_criteria_not_met",
        },
      ]);
    case "informed-consent":
      return buildFullRecordConfig(consentData, [
        {
          title: "Record",
          dataIndex: "consent_type",
          key: "consent_type",
        },
        {
          title: "Consent Sent Date",
          dataIndex: "consent_sent_date_derived",
          key: "consent_sent_date_derived",
        },
        {
          title: "Consent Signed Date",
          dataIndex: "ic_date_signed",
          key: "ic_date_signed",
        },
        { title: "IC Version", dataIndex: "ic_version", key: "ic_version" },
        {
          title: "Protocol Version",
          dataIndex: "protocol_version",
          key: "protocol_version",
        },
      ]);
    case "indication":
      return buildFullRecordConfig(
        [cardsData],
        [
          {
            title: "Date of first diagnosis",
            dataIndex: "first_dx_date",
            key: "first_dx_date",
          },
          {
            title: "Stage at first diagnosis",
            dataIndex: "first_dx_stage",
            key: "first_dx_stage",
          },
          {
            title: "Most Recent MMSE Score",
            dataIndex: "test_latest_score",
            key: "test_latest_score",
          },
          {
            title: "Most Recent MMSE Score Date",
            dataIndex: "test_latest_date_derived",
            key: "test_latest_date_derived",
          },
        ],
      );
    case "study-arm":
      return buildFullRecordConfig(
        [cardsData],
        [
          {
            title: "Study Arm Label",
            dataIndex: "study_arm_label",
            key: "study_arm_label",
          },
        ],
      );
    case "indication-events":
      return buildFullRecordConfig(indicationEventData, [
        {
          title: "Event name",
          dataIndex: "indication_event_name",
          key: "indication_event_name",
        },
        {
          title: "Onset date",
          dataIndex: "onset_date_derived",
          key: "onset_date_derived",
        },
        {
          title: "Treatment required",
          dataIndex: "treatment_required",
          key: "treatment_required",
        },
      ]);
    default:
      return { columns: [], dataSource: [] };
  }
};
